import { isString } from '../shared/runtime-values.js';
import type { Map as GlobeMap, ErrorEvent } from 'maplibre-gl';

const PREFERENCE_KEY = 'drawonamap.globe-terrain';

export function restoreGlobeTerrain(map: GlobeMap): void {
  let enabled = true;
  try { enabled = localStorage.getItem(PREFERENCE_KEY) !== 'false'; } catch {}
  applyGlobeTerrain(map, enabled);
}

const SOURCE = 'mapterhorn-terrain';
const TILE_URL = 'https://tiles.mapterhorn.com/{z}/{x}/{y}.webp';
// Use the provider's TileJSON credit, linked to its full upstream source list.
const ATTRIBUTION = '<a href="https://mapterhorn.com/attribution/" target="_blank" rel="noopener noreferrer">© Mapterhorn</a>';
const controllers = /* @__PURE__ */ new WeakMap<GlobeMap, ReturnType<typeof createTerrain>>();

function createTerrain(map: GlobeMap) {
  let phase: 'off' | 'loading' | 'ready' | 'failed' | 'removed' = 'off';
  let timer: ReturnType<typeof setTimeout> | undefined;
  let version = 0;
  const changed = () => window.dispatchEvent(new Event('globe-terrain-changed'));
  const clearTimer = () => { clearTimeout(timer); timer = undefined; };
  const set = (enabled: boolean) => {
    version++;
    clearTimer();
    if (phase === 'removed') return;
    phase = enabled ? 'loading' : 'off';
    if (enabled) {
      map.addSource(SOURCE, { type: 'raster-dem', tiles: [TILE_URL], encoding: 'terrarium', tileSize: 512, maxzoom: 12, attribution: ATTRIBUTION });
      map.setTerrain({ source: SOURCE, exaggeration: 1 });
      watch();
    } else {
      map.setTerrain(null);
      if (map.getSource(SOURCE)) map.removeSource(SOURCE);
    }
    changed();
  };
  const fallback = () => {
    if (phase !== 'loading' && phase !== 'ready') return;
    set(false);
    phase = 'failed';
    map.jumpTo({ pitch: 0 });
    changed();
  };
  const watch = () => {
    if (phase !== 'loading' && phase !== 'ready') return;
    if (map.isSourceLoaded(SOURCE)) { phase = 'ready'; clearTimer(); return; }
    timer ??= setTimeout(fallback, 15000);
  };
  const error = (event: ErrorEvent) => {
    if ((phase !== 'loading' && phase !== 'ready') || !isTerrainError(event)) return;
    const attempt = version;
    // Let MapLibre finish its source event before removing that source.
    queueMicrotask(() => { if (version === attempt) fallback(); });
  };
  const remove = () => {
    phase = 'removed';
    version++;
    clearTimer();
    map.off('error', error);
    map.off('render', watch);
    map.off('sourcedata', watch);
    map.off('sourcedataloading', watch);
    map.off('remove', remove);
    controllers.delete(map);
  };
  map.on('error', error);
  map.on('render', watch);
  map.on('sourcedata', watch);
  map.on('sourcedataloading', watch);
  map.on('remove', remove);
  return { set, state: () => ({ active: phase === 'loading' || phase === 'ready', failed: phase === 'failed' }) };
}

export function isTerrainError(event: { sourceId?: string; error: { message: string } }): boolean {
  return event.sourceId === SOURCE || ('url' in event.error && isString(event.error.url) && event.error.url.startsWith('https://tiles.mapterhorn.com/'));
}

export function setGlobeTerrain(map: GlobeMap, enabled: boolean): void {
  try { localStorage.setItem(PREFERENCE_KEY, String(enabled)); } catch {}
  applyGlobeTerrain(map, enabled);
}

function applyGlobeTerrain(map: GlobeMap, enabled: boolean): void {
  let controller = controllers.get(map);
  if (!controller) { controller = createTerrain(map); controllers.set(map, controller); }
  if (controller.state().active !== enabled) controller.set(enabled);
  if (enabled && map.getPitch() === 0) map.easeTo({ pitch: 45, duration: 250 });
}

export function globeTerrainState(map: GlobeMap | null): { active: boolean; failed: boolean } {
  return (map && controllers.get(map)?.state()) || { active: false, failed: false };
}

export function terrainExportAttribution(map: GlobeMap): string {
  return globeTerrainState(map).active ? 'Terrain: © Mapterhorn · mapterhorn.com/attribution/' : '';
}
